import React from 'react';
import {
  Box, Chip, LinearProgress, Stack, Typography, Tooltip,
} from '@mui/material';
import {
  Lock as LockIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  RemoveCircleOutline as MissingIcon,
} from '@mui/icons-material';
import type { ComplianceScore, ObligationStatus, DocumentStatus } from '../../api/services/document.service';
import {
  VaultTone, toneForObligation, toneForDocument, labelForObligation, labelForDocument,
  colorForTone, describeExpiry, describeScore,
} from './vaultStatus';

export * from './vaultStatus';

// The shared vault vocabulary for the web admin. Same restraint and the
// same status colours as the mobile vault, expressed in MUI.

// ------------------------------------------------------------ StatusPill

// Missing is hollow on purpose: an outline reads as absence, a filled pill
// reads as a thing that exists and has a state.
export const StatusPill: React.FC<{ tone: VaultTone; label: string; size?: 'small' | 'medium' }> = ({
  tone, label, size = 'small',
}) => {
  const color = colorForTone(tone);
  const hollow = tone === 'missing';
  return (
    <Chip
      size={size}
      label={label}
      variant={hollow ? 'outlined' : 'filled'}
      color={color === 'inherit' ? 'default' : color}
      sx={{
        fontWeight: 700,
        fontSize: 12,
        ...(hollow ? {} : { bgcolor: color === 'inherit' ? undefined : `${color}.light`, color: `${color}.dark` }),
      }}
    />
  );
};

export const ObligationPill: React.FC<{ status: ObligationStatus }> = ({ status }) => (
  <StatusPill tone={toneForObligation(status)} label={labelForObligation(status)} />
);

export const DocumentPill: React.FC<{ status: DocumentStatus }> = ({ status }) => (
  <StatusPill tone={toneForDocument(status)} label={labelForDocument(status)} />
);

// ------------------------------------------------------------ ExpiryLine

export const ExpiryLine: React.FC<{ expiresAt?: string | null; prefixWhenPast?: string }> = ({
  expiresAt, prefixWhenPast,
}) => {
  const text = describeExpiry(expiresAt, prefixWhenPast);
  if (!text) return null;
  const overdue = new Date(expiresAt as string).getTime() < Date.now();
  return (
    <Typography variant="caption" sx={{ color: overdue ? 'error.main' : 'text.secondary' }}>
      {text}
    </Typography>
  );
};

// --------------------------------------------------------- CoverageMeter

// A meter with no denominator is a lie. `score: null` means this owner has
// no obligations yet, which is NOT the same as being fully compliant, so it
// renders as "Not tracked" with an empty bar rather than a full green one.
export const CoverageMeter: React.FC<{ score: ComplianceScore | null; label?: string }> = ({ score, label }) => {
  const tracked = !!score && score.total > 0;
  const percent = tracked ? score!.percent : 0;
  const color = !tracked ? 'inherit' : percent === 100 ? 'success' : percent >= 80 ? 'warning' : 'error';

  return (
    <Box sx={{ minWidth: 160 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
        {label && <Typography variant="caption" color="text.secondary">{label}</Typography>}
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: tracked ? 'text.primary' : 'text.disabled' }}
        >
          {describeScore(score)}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={percent}
        color={color === 'inherit' ? 'inherit' : color}
        sx={{ height: 6, borderRadius: 3, opacity: tracked ? 1 : 0.35 }}
      />
    </Box>
  );
};

// ------------------------------------------------------------- FileGlyph

// No glyph at all when the document does not exist. A greyed-out page icon
// would still say "there is a file here".
export const FileGlyph: React.FC<{ contentType?: string; missing?: boolean }> = ({ contentType, missing }) => {
  if (missing) return <MissingIcon fontSize="small" sx={{ color: 'error.main', opacity: 0.7 }} />;
  if (contentType?.includes('pdf')) return <PdfIcon fontSize="small" sx={{ color: 'error.main' }} />;
  if (contentType?.startsWith('image/')) return <ImageIcon fontSize="small" sx={{ color: 'primary.main' }} />;
  return <FileIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
};

// -------------------------------------------------------------- LockChip

export const LockChip: React.FC<{ label?: string }> = ({ label = 'Restricted' }) => (
  <Tooltip title="Only certain roles can open this. Everyone else sees that it exists, and nothing more.">
    <Chip size="small" variant="outlined" icon={<LockIcon />} label={label} sx={{ fontSize: 11 }} />
  </Tooltip>
);

// --------------------------------------------------------- GeneratedName

// Filenames are generated by the backend from the type's namePattern, never
// typed by a person. Monospace so they read as system output.
export const GeneratedName: React.FC<{ name: string }> = ({ name }) => (
  <Typography
    component="span"
    sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13, wordBreak: 'break-all' }}
  >
    {name}
  </Typography>
);

// -------------------------------------------------------------- StatusDot

// The table's status column. A dot and a word: a row of full pills reads
// as a wall of colour, and in a list of forty documents the colour has to
// be scannable without being shouty.
export const StatusDot: React.FC<{ tone: VaultTone; label: string }> = ({ tone, label }) => {
  const color = colorForTone(tone);
  return (
    <Stack direction="row" gap={0.75} alignItems="center">
      <Box
        sx={{
          width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
          ...(tone === 'missing'
            // Missing is hollow here too — an absence is not a state the
            // document is in, because there is no document.
            ? { border: '1.5px solid', borderColor: 'error.main' }
            : { bgcolor: color === 'inherit' ? 'text.disabled' : `${color}.main` }),
        }}
      />
      <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{label}</Typography>
    </Stack>
  );
};
